import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Tab, Tabs, TabPanel, TabList } from 'react-tabs';

import { Album, Song } from 'interfaces';

import { AppLocale, BAND_ID, BAND_NAME, BAND_NAME_JP } from 'constant';

import { LanguageContext } from 'context/languageContext';

import Title from 'components/ui/Title';

import {
  Wrapper,
  TabsWrapper,
  People,
  DetailsList,
  DetailsListItem,
  DateWrapper,
  SecondaryDate,
  DescriptionWrapper
} from './SongDetails.styles';

export interface SongDetailsProps {
  song: Song;
  albumDetails: Album;
  internationalAlbumDetails: Album;
}

const SongDetails = ({ song, albumDetails, internationalAlbumDetails }: SongDetailsProps) => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const [selected, setSelected] = useState(0);

  return (
    <Wrapper>
      {
        albumDetails ? (
          <Title size="normal" color="primary" noMargin>
            {intl.formatMessage({ id: 'songs.songDetails.albumTitle' })}
            {locale === AppLocale.EN ? albumDetails.englishName || albumDetails.name : albumDetails.name}
          </Title>
        ) : null
      }
      <Title size="big" color="secondary">
        {intl.formatMessage({ id: 'songs.songDetails.title' })}
        {locale === AppLocale.EN ? song.englishName || song.name : song.name}
      </Title>
      <DetailsList>
        <DetailsListItem>
          <DateWrapper>
            {intl.formatMessage({ id: 'songs.songDetails.releaseDate' })}
            {intl.formatDate(song.singleRelease || albumDetails.releaseDate, {
              year: "numeric",
              month: "short",
              day: "numeric"
            })}
            {
              internationalAlbumDetails ? (
                <SecondaryDate>
                  {intl.formatMessage({ id: 'songs.songDetails.internationalReleaseDate' }, {
                    date: intl.formatDate(song.singleRelease || internationalAlbumDetails?.releaseDate, {
                      year: "numeric",
                      month: "short",
                      day: "numeric"
                    })
                  })}
                </SecondaryDate>
              ) : null
            }
          </DateWrapper>
        </DetailsListItem>
        <DetailsListItem>
          {intl.formatMessage({ id: 'songs.songDetails.duration' })}
          {song.duration}
        </DetailsListItem>
      </DetailsList>
      {
        song?.songInfo.length ? (
          <DescriptionWrapper>
            <Title size="normal" color="primary">
              {intl.formatMessage({ id: 'songs.description.title' })}
            </Title>
            {song?.songInfo?.map((desc, index) => (
              <p key={index}>{desc.info} / Confirmed: {desc.isConfirm ? 'yes' : 'no'}</p>
            ))}
          </DescriptionWrapper>
        ) : null
      }
      <TabsWrapper>
        <Tabs
          selectedIndex={selected}
          onSelect={(selected) => setSelected(selected)}
        >
          <TabList>
            <Tab>
              {intl.formatMessage({ id: 'songs.tabs.credits.title' })}
            </Tab>
            <Tab>
              {intl.formatMessage({ id: 'songs.tabs.lyrics.title' })}
            </Tab>
          </TabList>
          <TabPanel>
            <table>
            <tbody>
              <tr>
                <td>{intl.formatMessage({ id: 'songs.songDetails.lyrics.title' })}</td>
                <td>
                  {
                    song.songDetails?.lyrics?.length ? (
                      <People>
                        {
                          song.songDetails?.lyrics.map((el) => {
                            const link = el === BAND_ID ? `/about` : `/about/${el}`;
                            return (
                              <span key={`people_${el}`}>
                                <Link to={link}>
                                {intl.formatMessage({ id: `people.${el}` }, {
                                  bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                                })}
                              </Link>
                              </span>
                            )
                          })
                        }
                      </People>
                    ) : '-'
                  }
                </td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'songs.songDetails.music.title' })}</td>
                <td>
                  {
                    song.songDetails?.music?.length ? (
                      <People>
                        {
                          song.songDetails?.music.map((el) => {
                            const link = el === BAND_ID ? `/about` : `/about/${el}`;
                            return (
                              <span key={`people_${el}`}>
                                <Link to={link}>
                                {intl.formatMessage({ id: `people.${el}` }, {
                                  bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                                })}
                              </Link>
                              </span>
                            )
                          })
                        }
                      </People>
                    ) : '-'
                  }
                </td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'songs.songDetails.arrangement.title' })}</td>
                <td>
                  {
                    song.songDetails?.arrangement?.length ? (
                      <People>
                        {
                          song.songDetails?.arrangement.map((el) => {
                            const link = el === BAND_ID ? `/about` : `/about/${el}`;
                            return (
                              <span key={`people_${el}`}>
                                <Link to={link}>
                                  {intl.formatMessage({ id: `people.${el}` }, {
                                    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                                  })}
                                </Link>
                              </span>
                            )
                          })
                        }
                      </People>
                    ) : '-'
                  }
                </td>
              </tr>
              <tr>
                <td>{intl.formatMessage({ id: 'songs.songDetails.production.title' })}</td>
                <td>
                  {
                    song.songDetails?.production?.length ? (
                      <People>
                        {
                          song.songDetails?.production.map((el) => {
                            const link = el === BAND_ID ? `/about` : `/about/${el}`;
                            return (
                              <span key={`people_${el}`}>
                                <Link to={link}>
                                  {intl.formatMessage({ id: `people.${el}` }, {
                                    bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                                  })}
                                </Link>
                              </span>
                            )
                          })
                        }
                      </People>
                    ) : '-'
                  }
                </td>
              </tr>
            </tbody>
            </table>
          </TabPanel>
          <TabPanel>
            <p>[WORK IN PROGRESS]</p>
          </TabPanel>
        </Tabs>
      </TabsWrapper>

    </Wrapper>
  );
};

export default SongDetails;
